<template>
  <t-config-provider>
    <router-view v-if="isPublicPage" />

    <t-layout v-else class="admin-shell">
      <t-aside
        class="admin-shell__aside"
        :class="{ 'admin-shell__aside--collapsed': collapsed }"
      >
        <div class="brand">
          <div class="brand__mark">GP</div>
          <div v-show="!collapsed" class="brand__text">
            <strong>GamePlanet</strong>
            <span>后台管理系统</span>
          </div>
        </div>
        <admin-menu :collapsed="collapsed" />
      </t-aside>

      <t-layout class="admin-shell__main">
        <t-header class="admin-header">
          <div class="admin-header__left">
            <t-button
              class="admin-header__menu"
              shape="square"
              variant="text"
              @click="handleMenuButton"
            >
              <template #icon>
                <menu-fold-icon v-if="!collapsed || isMobile" />
                <menu-unfold-icon v-else />
              </template>
            </t-button>
            <div class="page-title">
              <strong>{{ currentPageTitle }}</strong>
              <span>{{ currentPageGroup }}</span>
            </div>
          </div>

          <div class="admin-header__right">
            <t-tag theme="primary" variant="light">{{ roleName }}</t-tag>
            <t-dropdown :options="userOptions" trigger="click" @click="handleUserAction">
              <t-button variant="text" class="user-button">
                <template #icon><user-circle-icon /></template>
                <span>{{ username || '管理员' }}</span>
              </t-button>
            </t-dropdown>
          </div>
        </t-header>

        <t-content class="admin-content">
          <router-view />
        </t-content>
      </t-layout>

      <t-drawer
        v-model:visible="drawerVisible"
        size="280px"
        placement="left"
        :footer="false"
        :header="false"
        class="mobile-drawer"
      >
        <div class="brand brand--drawer">
          <div class="brand__mark">GP</div>
          <div class="brand__text">
            <strong>GamePlanet</strong>
            <span>后台管理系统</span>
          </div>
        </div>
        <admin-menu @navigate="drawerVisible = false" />
      </t-drawer>
    </t-layout>
  </t-config-provider>
</template>

<script>
import { computed, defineComponent, h, onBeforeUnmount, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { Menu, MenuItem, Submenu } from 'tdesign-vue-next';
import {
  BarcodeIcon,
  // CloudIcon,
  DashboardIcon,
  EditIcon,
  FileCodeIcon,
  GamepadIcon,
  HomeIcon,
  LogoutIcon,
  MenuFoldIcon,
  MenuUnfoldIcon,
  MoneyIcon,
  NotificationIcon,
  QrcodeIcon,
  SettingIcon,
  TicketIcon,
  UserCircleIcon,
  UserIcon,
} from 'tdesign-icons-vue-next';

const adminRoutes = [
  {
    value: 'dashboard',
    title: '仪表盘',
    icon: DashboardIcon,
    children: [{ value: '/', title: '首页', icon: HomeIcon }],
  },
  {
    value: 'code',
    title: '卡密管理',
    icon: QrcodeIcon,
    children: [
      { value: '/generateCodeZS', title: '赠送系统', icon: TicketIcon },
      { value: '/generateCode', title: '生成卡密', icon: BarcodeIcon },
      { value: '/codeRecords', title: '兑换记录', icon: FileCodeIcon },
    ],
  },
  {
    value: 'games',
    title: '游戏管理',
    icon: GamepadIcon,
    children: [
      { value: '/gameList', title: '游戏列表', icon: GamepadIcon },
      { value: '/updatehistorys', title: '更新日志', icon: EditIcon },
      // { value: '/steamdb', title: '检索新游', icon: DashboardIcon, adminOnly: true },
      { value: '/gameDepots', title: 'DLC管理', icon: FileCodeIcon, adminOnly: true },
      { value: '/gametickets', title: '票证管理', icon: TicketIcon, adminOnly: true },
      { value: '/depotsManagement', title: 'Depots管理', icon: SettingIcon, adminOnly: true },
      // { value: '/eticketStats', title: 'ETicket管理', icon: TicketIcon, adminOnly: true },
      { value: '/denuvoTicketUpload', title: 'ETicket管理', icon: TicketIcon, adminOnly: true },
    ],
  },
  {
    value: 'users',
    title: '用户管理',
    icon: UserIcon,
    children: [
      { value: '/lotteryRecord', title: '抽奖记录', icon: TicketIcon, adminOnly: true },
      { value: '/order', title: '订单管理', icon: MoneyIcon, adminOnly: true },
      { value: '/registeredUsers', title: '用户管理', icon: UserIcon },
      // { value: '/onlineHeartbeat', title: '在线心跳', icon: UserIcon },
      // { value: '/cloudStorageManage', title: '存档管理', icon: CloudIcon, adminOnly: true },
      { value: '/cleanRecordForTest', title: '清理测试', icon: FileCodeIcon },
      // { value: '/denuvoTicketCandidates', title: 'D加密候选', icon: UserIcon, adminOnly: true },
    ],
  },
  {
    value: 'admin',
    title: '后台管理',
    icon: SettingIcon,
    children: [
      { value: '/orderVerification', title: '订单核查', icon: TicketIcon },
      { value: '/announcements', title: '发布公告', icon: NotificationIcon },
      { value: '/distributors', title: '经销商管理', icon: UserCircleIcon, adminOnly: true },
      { value: '/ManageApplyDistributors', title: '待审核加盟', icon: UserCircleIcon, adminOnly: true },
      { value: '/salesOverview', title: '经销商状况', icon: MoneyIcon, adminOnly: true },
      { value: '/addDistributor', title: '添加经销商', icon: UserCircleIcon, adminOnly: true },
      { value: '/codeSettings', title: '权限组管理', icon: SettingIcon, adminOnly: true },
      { value: '/editScore', title: '信用分管理', icon: EditIcon, adminOnly: true },
      { value: '/badPersonReview', title: '公安不良审查', icon: UserIcon, adminOnly: true },
      { value: '/dashboard', title: '更多功能', icon: DashboardIcon, adminOnly: true },
    ],
  },
];

const flattenRoutes = adminRoutes.flatMap((group) =>
  group.children.map((item) => ({ ...item, group: group.title }))
);

const AdminMenu = defineComponent({
  name: 'AdminMenu',
  props: {
    collapsed: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['navigate'],
  setup(props, { emit }) {
    const route = useRoute();
    const router = useRouter();
    const store = useStore();
    const owner = computed(() => store.state.user.owner);
    const visibleRoutes = computed(() =>
      adminRoutes.map((group) => ({
        ...group,
        children: group.children.filter((item) => !item.adminOnly || owner.value === '0'),
      }))
    );

    const renderIcon = (Icon) => () => h(Icon);
    const handleChange = (value) => {
      router.push(value);
      emit('navigate');
    };

    return () =>
      h(
        Menu,
        {
          value: route.path,
          collapsed: props.collapsed,
          width: '100%',
          defaultExpanded: visibleRoutes.value.map((group) => group.value),
          expandMutex: false,
          class: 'admin-menu',
          onChange: handleChange,
        },
        () =>
          visibleRoutes.value.map((group) =>
            h(
              Submenu,
              { value: group.value },
              {
                icon: renderIcon(group.icon),
                title: () => group.title,
                default: () =>
                  group.children.map((item) =>
                    h(
                      MenuItem,
                      { value: item.value },
                      {
                        default: () => item.title,
                      }
                    )
                  ),
              }
            )
          )
      );
  },
});

export default {
  name: 'App',
  components: {
    AdminMenu,
    MenuFoldIcon,
    MenuUnfoldIcon,
    UserCircleIcon,
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const store = useStore();
    const isMobile = ref(window.innerWidth < 900);
    const collapsed = ref(false);
    const drawerVisible = ref(false);

    const username = computed(() => store.state.user.username);
    const owner = computed(() => store.state.user.owner);
    const roleName = computed(() => (owner.value === '0' ? '超级管理员' : '经销商'));
    const isPublicPage = computed(() => route.meta.hideSidebar);
    const currentRouteMeta = computed(() => flattenRoutes.find((item) => item.value === route.path));
    const currentPageTitle = computed(() => currentRouteMeta.value?.title || '后台管理');
    const currentPageGroup = computed(() => currentRouteMeta.value?.group || 'GamePlanet');
    const userOptions = computed(() => [
      {
        content: '退出登录',
        value: 'logout',
        prefixIcon: () => h(LogoutIcon),
      },
    ]);

    const handleResize = () => {
      isMobile.value = window.innerWidth < 900;
      if (isMobile.value) {
        collapsed.value = false;
      }
    };

    const handleMenuButton = () => {
      if (isMobile.value) {
        drawerVisible.value = true;
        return;
      }
      collapsed.value = !collapsed.value;
    };

    const handleUserAction = ({ value }) => {
      if (value !== 'logout') return;
      store.dispatch('logout');
      router.push({ name: 'UserLogin' });
    };

    onMounted(() => {
      handleResize();
      window.addEventListener('resize', handleResize);
    });

    onBeforeUnmount(() => {
      window.removeEventListener('resize', handleResize);
    });

    return {
      collapsed,
      currentPageGroup,
      currentPageTitle,
      drawerVisible,
      handleMenuButton,
      handleUserAction,
      isMobile,
      isPublicPage,
      roleName,
      userOptions,
      username,
    };
  },
};
</script>
